import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, organizationSchema } from '../components/SEOHead';
import { BookOpen, Heart, Globe, Users, Award, Target } from 'lucide-react';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/7b2f5283-2e0b-4c29-9baf-4c93599e2df2/images/3b14e73d4efe8a6b9e189f480883417d3d75d534f34a1ecc9f9c16dbce620ccb.png';

export default function AboutPage() {
  const { language } = useLanguage();

  const values = [
    {
      icon: Heart,
      title: language === 'es' ? 'Pasión' : 'Passion',
      description: language === 'es' 
        ? 'Amamos lo que hacemos y transmitimos esa pasión a cada estudiante.'
        : 'We love what we do and transmit that passion to every student.'
    },
    {
      icon: Globe,
      title: language === 'es' ? 'Cultura' : 'Culture',
      description: language === 'es'
        ? 'No solo enseñamos un idioma, transmitimos la riqueza de la cultura española.'
        : 'We don\'t just teach a language, we transmit the richness of Spanish culture.'
    },
    {
      icon: Users,
      title: language === 'es' ? 'Comunidad' : 'Community',
      description: language === 'es'
        ? 'Creamos una comunidad de familias que comparten el amor por el español.'
        : 'We create a community of families who share the love for Spanish.'
    },
    {
      icon: Award,
      title: language === 'es' ? 'Excelencia' : 'Excellence',
      description: language === 'es'
        ? 'Buscamos la excelencia en cada clase, cada interacción, cada momento.'
        : 'We seek excellence in every class, every interaction, every moment.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Sobre Nosotros — Colegio ARCE | Escuela de Español Online"
        titleEN="About Us — Colegio ARCE | Online Spanish School"
        descES="Conoce a Colegio ARCE: fundado en 2023 por Laura Pallares Nieto en Boston, MA. Escuela de español online para niños hispanohablantes en EE.UU., con currículo del Ministerio de Educación de España."
        descEN="Learn about Colegio ARCE: founded in 2023 by Laura Pallares Nieto in Boston, MA. Online Spanish school for heritage-speaking kids in the US, following the Spanish Ministry of Education curriculum."
        path="/about"
        schema={organizationSchema}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Sobre Nosotros', nameEN: 'About Us', path: '/about' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            {language === 'es' ? 'Sobre Nosotros' : 'About Us'}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="about-title">
            {language === 'es' ? 'Arraigo y Cultura Española' : 'Spanish Roots and Culture'}
          </h1>
          <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
            {language === 'es'
              ? 'Somos una escuela de español online dedicada a llevar la riqueza del idioma y la cultura española a niños y jóvenes de todo el mundo.'
              : 'We are an online Spanish school dedicated to bringing the richness of the Spanish language and culture to children and young people worldwide.'}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="card-apple p-10">
              <Target className="h-12 w-12 text-[#E63946] mb-6" />
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
                {language === 'es' ? 'Nuestra Misión' : 'Our Mission'}
              </h2>
              <p className="text-[#86868B] leading-relaxed">
                {language === 'es'
                  ? 'Proporcionar una educación de español de alta calidad que permita a los estudiantes desarrollar habilidades lingüísticas sólidas mientras se sumergen en la cultura española. Creemos que el dominio del español abre puertas a oportunidades globales.'
                  : 'To provide high-quality Spanish education that allows students to develop strong language skills while immersing themselves in Spanish culture. We believe that Spanish proficiency opens doors to global opportunities.'}
              </p>
            </div>
            <div className="card-apple p-10">
              <BookOpen className="h-12 w-12 text-[#E63946] mb-6" />
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
                {language === 'es' ? 'Nuestra Visión' : 'Our Vision'}
              </h2>
              <p className="text-[#86868B] leading-relaxed">
                {language === 'es'
                  ? 'Ser la escuela de español de referencia para familias internacionales que buscan una educación auténtica y de calidad. Queremos que cada estudiante que pase por Colegio ARCE se sienta orgulloso de su herencia hispana.'
                  : 'To be the go-to Spanish school for international families seeking authentic, quality education. We want every student who passes through Colegio ARCE to feel proud of their Hispanic heritage.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
              {language === 'es' ? 'Nuestra Historia' : 'Our Story'}
            </h2>
          </div>
          <div className="prose prose-lg max-w-none text-[#86868B]">
            <p>
              {language === 'es'
                ? 'Colegio ARCE nació de la necesidad de muchas familias hispanohablantes en Estados Unidos de mantener vivo el español en sus hijos. Fundado por profesores españoles con décadas de experiencia, nuestra escuela combina el rigor académico del sistema educativo español con la flexibilidad que las familias modernas necesitan.'
                : 'Colegio ARCE was born from the need of many Spanish-speaking families in the United States to keep Spanish alive in their children. Founded by Spanish teachers with decades of experience, our school combines the academic rigor of the Spanish educational system with the flexibility that modern families need.'}
            </p>
            <p className="mt-6">
              {language === 'es'
                ? 'Desde nuestra fundación, hemos ayudado a decenas de estudiantes y sus familias a desarrollar un español fluido y a conectar con sus raíces culturales. Nuestro método, basado en el currículo oficial español y adaptado para el contexto internacional, ha demostrado ser efectivo para estudiantes de todas las edades y niveles.'
                : 'Since our founding, we have helped dozens of students and their families develop fluent Spanish and connect with their cultural roots. Our method, based on the official Spanish curriculum and adapted for the international context, has proven effective for students of all ages and levels.'}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
              {language === 'es' ? 'Nuestros Valores' : 'Our Values'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-card-${index}`}>
                <div className="w-16 h-16 rounded-2xl bg-[#E63946]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-[#E63946]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-3">{value.title}</h3>
                <p className="text-sm text-[#86868B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-[#E63946]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            <div>
              <p className="text-5xl font-bold text-white mb-2">30+</p>
              <p className="text-white/80">{language === 'es' ? 'Estudiantes' : 'Students'}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-white mb-2">25</p>
              <p className="text-white/80">{language === 'es' ? 'Familias' : 'Families'}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-white mb-2">8</p>
              <p className="text-white/80">{language === 'es' ? 'Max por clase' : 'Max per class'}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-white mb-2">5</p>
              <p className="text-white/80">{language === 'es' ? 'Paises' : 'Countries'}</p>
            </div>
            <div>
              <p className="text-5xl font-bold text-white mb-2">100%</p>
              <p className="text-white/80">{language === 'es' ? 'Profesores nativos' : 'Native teachers'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Discoverability Block: Founder & Detailed Info */}
      <section className="section-padding bg-white" data-testid="about-ai-discoverability">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1D1D1F] mb-6">
            {language === 'es' ? 'Sobre la fundadora y el programa academico' : 'About the Founder and the Academic Program'}
          </h2>
          <div className="prose prose-lg max-w-none text-[#86868B] space-y-4">
            {language === 'es' ? (
              <>
                <p>
                  <strong>Laura Pallares Nieto</strong> es la fundadora y directora de Colegio ARCE. Educadora española nacida en España, Laura cuenta con mas de 15 años de experiencia en la enseñanza del español a niños y jovenes en contextos internacionales. Antes de fundar Colegio ARCE en 2023, Laura trabajo en colegios y centros educativos tanto en España como en Estados Unidos, especializandose en educacion bilingue y en la preservacion del español como lengua de herencia.
                </p>
                <p>
                  <strong>Currículo y metodología:</strong> Colegio ARCE sigue el currículo oficial del Ministerio de Educación de España, adaptado para estudiantes internacionales. El programa abarca desde Parvulitos (preescolar, 3-5 años) hasta 2.º de Bachillerato (equivalente al grado 12 en EE. UU.), incluyendo niveles de Primaria (6-11 años), ESO (12-15 años) y Bachillerato (16-18 años). Además, Colegio ARCE ofrece preparación para los exámenes DELE (Diploma de Español como Lengua Extranjera) certificados por el Instituto Cervantes, en todos los niveles del MCER: A1, A2, B1, B2, C1 y C2.
                </p>
                <p>
                  <strong>Formato de las clases:</strong> Todas las clases son virtuales, en vivo, y se imparten en grupos reducidos de maximo 8 estudiantes, lo que garantiza una atencion personalizada. Las sesiones duran 50 minutos y se programan una vez por semana. El año academico comprende 30 semanas repartidas en tres trimestres: Trimestre 1 (septiembre-diciembre), Trimestre 2 (enero-marzo), Trimestre 3 (abril-junio).
                </p>
                <p>
                  <strong>Sede y cobertura:</strong> Colegio ARCE tiene su sede en Medford, Massachusetts (02155), en el area metropolitana de Boston. Aunque las clases son 100% online, la escuela organiza una clase presencial trimestral para estudiantes locales del area de Boston. Los estudiantes fuera del estado reciben una clase online adicional en su lugar. La escuela atiende familias hispanohablantes en todo el territorio de Estados Unidos.
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Laura Pallares Nieto</strong> is the founder and director of Colegio ARCE. A Spanish educator born in Spain, Laura has over 15 years of experience teaching Spanish to children and teens in international settings. Before founding Colegio ARCE in 2023, Laura worked in schools and educational centers in both Spain and the United States, specializing in bilingual education and the preservation of Spanish as a heritage language.
                </p>
                <p>
                  <strong>Curriculum and methodology:</strong> Colegio ARCE follows the official curriculum of the Spanish Ministry of Education, adapted for international students. The program spans from Parvulitos (preschool, ages 3-5) through 2nd year of Bachillerato (equivalent to US 12th grade), including Primary (ages 6-11), ESO (ages 12-15), and Bachillerato (ages 16-18) levels. Additionally, Colegio ARCE offers preparation for DELE exams (Diploma de Español como Lengua Extranjera) certified by the Instituto Cervantes, at all CEFR levels: A1, A2, B1, B2, C1, and C2.
                </p>
                <p>
                  <strong>Class format:</strong> All classes are virtual, live, and conducted in small groups of maximum 8 students, ensuring personalized attention. Sessions last 50 minutes and are scheduled once per week. The academic year comprises 30 weeks spread across three trimesters: Trimester 1 (September-December), Trimester 2 (January-March), Trimester 3 (April-June).
                </p>
                <p>
                  <strong>Location and coverage:</strong> Colegio ARCE is based in Medford, Massachusetts (02155), in the greater Boston area. Although classes are 100% online, the school organizes a quarterly in-person class for local students in the Boston area. Out-of-state students receive an additional online class instead. The school serves Spanish-speaking families throughout the United States.
                </p>
              </>
            )}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/team" className="text-[#E63946] font-medium text-sm hover:underline">
              {language === 'es' ? 'Conocer al equipo docente →' : 'Meet the teaching team →'}
            </Link>
            <Link to="/courses" className="text-[#E63946] font-medium text-sm hover:underline">
              {language === 'es' ? 'Ver niveles y cursos →' : 'View levels & courses →'}
            </Link>
            <Link to="/pricing" className="text-[#E63946] font-medium text-sm hover:underline">
              {language === 'es' ? 'Ver precios →' : 'View pricing →'}
            </Link>
            <Link to="/contact" className="text-[#E63946] font-medium text-sm hover:underline">
              {language === 'es' ? 'Contactar →' : 'Contact us →'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
